"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Mail, BookOpen } from "lucide-react";

// Temporary kill switch for the GoHighLevel / LeadConnector livechat widget.
// While `false` the third-party loader script never loads. The Help button is
// NOT a no-op regardless: openChatWidget() always falls back to a first-party
// Help panel (see HelpFallbackModal), so a click is never a silent dead-end.
// Flip back to `true` to re-enable the live chat bubble everywhere.
const CHAT_WIDGET_ENABLED: boolean = false;

// First-party support channels — mirror the Terms "Contact" section so the
// fallback panel always points somewhere real even when live chat is off.
const SUPPORT_EMAIL = "help@panda.new";
const SUPPORT_DOCS_URL = "https://docs.panda.new";
const SUPPORT_SUBJECT = "Help with Panda";

const HIDDEN_PREFIXES = ["/project/"];
// Paths where the widget is hidden on mobile only (it overlaps the primary
// input/CTA on the small-screen home page). Matches the Tailwind `sm`
// breakpoint the home page uses to switch between mobile and desktop layouts.
const HIDDEN_ON_MOBILE_PATHS = ["/"];
const MOBILE_MEDIA = "(max-width: 639px)";
const HIDE_STYLE_ID = "panda-hide-chat-widget";

// How long to wait for the live widget to finish loading after a click before
// giving up and showing the first-party fallback, and how often to re-check.
const WIDGET_POLL_INTERVAL_MS = 300;
const WIDGET_POLL_TIMEOUT_MS = 6000;

export type WidgetState = "ready" | "loading" | "unavailable";

/**
 * Pure classifier for the live chat widget's readiness. Exported for tests so
 * the loaded / loading / unavailable states can be asserted without a real
 * third-party script. `enabled` is the kill switch, `widget` is the
 * <chat-widget> element (null until the loader injects it), and `hasLauncher`
 * is whether its clickable bubble exists yet.
 */
export function classifyWidget(
  enabled: boolean,
  widget: Element | null,
  hasLauncher: boolean,
): WidgetState {
  if (!enabled) return "unavailable";
  if (!widget || !hasLauncher) return "loading";
  return "ready";
}

/** Maps a widget state to what the shared Help handler should do. */
export function resolveHelpAction(
  state: WidgetState,
): "open-widget" | "queue" | "fallback" {
  if (state === "ready") return "open-widget";
  if (state === "loading") return "queue";
  return "fallback";
}

// --- First-party fallback pub/sub -------------------------------------------
// The imperative openChatWidget() handler and the declarative
// <HelpFallbackModal> live in different trees, so a tiny module-level store
// bridges them. openHelpFallback() notifies every mounted modal. A single
// modal is mounted globally (app/layout.tsx), so repeated calls just re-open
// the one panel rather than stacking overlays.
type HelpListener = () => void;
const helpListeners = new Set<HelpListener>();

function subscribeHelpFallback(listener: HelpListener): () => void {
  helpListeners.add(listener);
  return () => {
    helpListeners.delete(listener);
  };
}

function openHelpFallback(): void {
  if (helpListeners.size > 0) {
    helpListeners.forEach((listener) => listener());
    return;
  }
  // Last resort: the Help code ran on a page with no <ChatWidget> mounted.
  // Still do something visible rather than nothing — open the support email.
  if (typeof window !== "undefined") {
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      SUPPORT_SUBJECT,
    )}`;
  }
}

// --- Live widget lookup + open ----------------------------------------------
function findLauncher(widget: Element | null): HTMLElement | null {
  return (
    widget?.shadowRoot?.querySelector<HTMLElement>(
      "button, [class*='bubble'], [class*='launcher'], [class*='toggle']",
    ) ?? null
  );
}

function currentWidgetState(): WidgetState {
  if (typeof document === "undefined") return "unavailable";
  const widget = document.querySelector("chat-widget");
  return classifyWidget(CHAT_WIDGET_ENABLED, widget, !!findLauncher(widget));
}

/** Opens the live widget when it's ready. Returns true when it did. */
function tryOpenLiveWidget(): boolean {
  if (typeof document === "undefined") return false;
  const widget = document.querySelector("chat-widget") as HTMLElement | null;
  const launcher = findLauncher(widget);
  if (!widget || !launcher) return false;
  document.getElementById(HIDE_STYLE_ID)?.remove();
  widget.click();
  launcher.click();
  return true;
}

let widgetPollTimer: ReturnType<typeof setInterval> | null = null;

function stopWidgetPoll(): void {
  if (widgetPollTimer !== null) {
    clearInterval(widgetPollTimer);
    widgetPollTimer = null;
  }
}

/**
 * The widget was still loading when the user clicked. Queue the open: re-check
 * on an interval and open as soon as it's ready, or fall back to the first-party
 * panel if it never initialises. Only one poll runs at a time, so repeated
 * clicks never stack timers or open duplicate surfaces.
 */
function queueWidgetOpen(): void {
  if (widgetPollTimer !== null) return; // already queued
  const deadline = Date.now() + WIDGET_POLL_TIMEOUT_MS;
  widgetPollTimer = setInterval(() => {
    if (tryOpenLiveWidget()) {
      stopWidgetPoll();
      return;
    }
    if (Date.now() >= deadline) {
      stopWidgetPoll();
      openHelpFallback();
    }
  }, WIDGET_POLL_INTERVAL_MS);
}

/**
 * The single shared Help handler for every surface — the desktop header
 * question-mark button, the mobile Project Actions sheet, and the project
 * settings menu all call this, so they can never diverge. It always produces a
 * visible result: it opens the live chat widget when ready, queues the open
 * while the widget is still loading, and falls back to a first-party Help panel
 * when the widget is disabled or fails to initialise. Never a silent no-op.
 */
export function openChatWidget(): void {
  if (typeof window === "undefined") return;
  const action = resolveHelpAction(currentWidgetState());
  if (action === "open-widget") {
    if (!tryOpenLiveWidget()) openHelpFallback();
    return;
  }
  if (action === "queue") {
    queueWidgetOpen();
    return;
  }
  openHelpFallback();
}

/**
 * First-party Help panel. Mounted once, globally (via <ChatWidget> in the root
 * layout), and shown whenever openChatWidget() has to fall back — i.e. the live
 * chat widget is disabled, still loading past the timeout, or failed to load.
 * It renders above the Build canvas and any open modal, traps focus, closes on
 * Escape / backdrop, and restores focus to the trigger on close.
 */
export function HelpFallbackModal() {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  // Subscribe to the shared fallback signal. Opening while already open is a
  // no-op (single boolean), so repeated Help clicks never stack overlays.
  useEffect(() => {
    return subscribeHelpFallback(() => {
      lastFocusedRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
      setOpen(true);
    });
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Focus management: move focus into the dialog on open, trap Tab within it,
  // close on Escape, and restore focus to the trigger (the Help button) on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = lastFocusedRef.current;
    closeRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const rowCls =
    "flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600";

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="panda-help-title"
        aria-describedby="panda-help-desc"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-t-2xl border border-zinc-200 bg-white p-6 shadow-2xl dark:border-[#334155] dark:bg-[#18212f] sm:rounded-2xl"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={close}
          aria-label="Close help"
          className="absolute right-4 top-4 rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 dark:hover:bg-[#334155] dark:hover:text-zinc-300"
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          id="panda-help-title"
          className="pr-8 text-lg font-bold text-zinc-900 dark:text-[#f1f5f9]"
        >
          Need a hand?
        </h2>
        <p
          id="panda-help-desc"
          className="mt-1 text-sm text-zinc-500 dark:text-[#94a3b8]"
        >
          Reach the Panda team directly, or browse the docs for quick answers.
        </p>

        <div className="mt-5 space-y-2">
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(SUPPORT_SUBJECT)}`}
            onClick={close}
            className={`${rowCls} border-transparent bg-panda-gradient-success text-white shadow-lg shadow-emerald-900/20 hover:brightness-110`}
          >
            <Mail className="h-4 w-4 shrink-0" />
            <span className="flex flex-col text-left">
              <span>Message support</span>
              <span className="text-[11px] font-normal text-white/80">
                {SUPPORT_EMAIL}
              </span>
            </span>
          </a>

          <a
            href={SUPPORT_DOCS_URL}
            target="_blank"
            rel="noreferrer"
            onClick={close}
            className={`${rowCls} border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-[#334155] dark:text-[#f1f5f9] dark:hover:bg-white/[0.04]`}
          >
            <BookOpen className="h-4 w-4 shrink-0" />
            <span className="flex flex-col text-left">
              <span>Browse documentation</span>
              <span className="text-[11px] font-normal text-zinc-400 dark:text-[#64748b]">
                docs.panda.new
              </span>
            </span>
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ChatWidget() {
  const pathname = usePathname();
  const hideAlways = HIDDEN_PREFIXES.some((p) => pathname?.startsWith(p));
  const hideOnMobile = HIDDEN_ON_MOBILE_PATHS.some((p) => pathname === p);

  useEffect(() => {
    if (!CHAT_WIDGET_ENABLED) return;
    if (!hideAlways && !hideOnMobile) {
      document.getElementById(HIDE_STYLE_ID)?.remove();
      return;
    }
    const css = hideAlways
      ? `chat-widget { display: none !important; }`
      : `@media ${MOBILE_MEDIA} { chat-widget { display: none !important; } }`;
    const existing = document.getElementById(HIDE_STYLE_ID);
    if (existing) {
      existing.textContent = css;
    } else {
      const style = document.createElement("style");
      style.id = HIDE_STYLE_ID;
      style.textContent = css;
      document.head.appendChild(style);
    }
    return () => {
      document.getElementById(HIDE_STYLE_ID)?.remove();
    };
  }, [hideAlways, hideOnMobile]);

  // The fallback panel is always mounted so openChatWidget() has somewhere to
  // land regardless of the kill switch. The live-chat loader only mounts when
  // the widget is enabled.
  return (
    <>
      {CHAT_WIDGET_ENABLED && (
        <Script
          src="https://widgets.leadconnectorhq.com/loader.js"
          data-resources-url="https://widgets.leadconnectorhq.com/chat-widget/loader.js"
          data-widget-id="6a031223205bf865008605dc"
          strategy="afterInteractive"
          // Without `crossorigin`, anything this widget throws reaches us as the
          // browser's muted "Script error." — no message, no file, no stack (see
          // the block comment in app/layout.tsx). The CDN serves
          // `Access-Control-Allow-Origin: *`, so asking for CORS costs nothing
          // and buys a real stack the next time the widget breaks.
          crossOrigin="anonymous"
        />
      )}
      <HelpFallbackModal />
    </>
  );
}
